/**
 * Loading one project's whole workspace, and remembering it (2026-09-22).
 *
 * Lives outside the view for two reasons: the load is the expensive thing and
 * deserves tests of its own, and every screen that WRITES a project's data
 * needs to invalidate the cache without importing a view to do it.
 */

import { getProject } from '@/lib/data/projects';
import { listRooms } from '@/lib/data/rooms';
import { listContacts } from '@/lib/data/contacts';
import { listNotes } from '@/lib/data/notes';
import { listProjectRoomColors } from '@/lib/data/palette';
import { listSpecsForProject } from '@/lib/data/specs';
import { listFiles } from '@/lib/data/files';
import { getQuestionnaire } from '@/lib/data/questionnaires';
import { listPunchForProject } from '@/lib/data/punch';
import { listScheduleForProject } from '@/lib/data/schedule';
import { listChangeOrders, listChangeOrderEvents } from '@/lib/data/change-orders';
import { listAssignmentsForProject, listPortalTokensForProject } from '@/lib/data/subcontractors';
import { listMaterialCategories } from '@/lib/data/material-categories';
import { listBudgetCategories, listBudgetLines, getProjectBudget } from '@/lib/data/budget';
import { listOpenFeedbackForProject } from '@/lib/data/room-feedback';
import {
  listNotificationsForProject, listPendingNotifications, cancelStaleNotifications,
  type NotificationRow,
} from '@/lib/data/notifications';
import { staleCutoffIso, isStaleReminder } from '@/lib/notification-messages';
import { PageCache } from '@/lib/page-cache';
import type {
  ProjectRow, RoomRow, DesignerContactRow, NoteRow, PaletteColorRow, SpecRow, FileRow,
  QuestionnaireRow, PunchItemRow, ScheduleEntryRow, ChangeOrderRow, ChangeOrderEventRow,
  ContractorAssignmentRow,
  ContractorPortalTokenRow, MaterialCategoryRow, BudgetCategoryRow, BudgetLineRow,
  ProjectBudgetRow, RoomFeedbackRow,
} from '@/types/database';

/**
 * Everything one project page shows. Loaded together, cached together.
 */
export interface ProjectWorkspace {
  p: ProjectRow;
  rooms: RoomRow[];
  contacts: DesignerContactRow[];
  notes: NoteRow[];
  roomColors: PaletteColorRow[];
  specs: SpecRow[];
  files: FileRow[];
  questionnaire: QuestionnaireRow | null;
  punch: PunchItemRow[];
  schedule: ScheduleEntryRow[];
  notifications: NotificationRow[];
  changeOrders: ChangeOrderRow[];
  /** 0046 — every change order's trail, for the timeline and the question flag. */
  changeOrderEvents: ChangeOrderEventRow[];
  assignments: ContractorAssignmentRow[];
  materialCategories: MaterialCategoryRow[];
  budgetCategories: BudgetCategoryRow[];
  budgetLines: BudgetLineRow[];
  projectBudget: ProjectBudgetRow | null;
  openFeedback: RoomFeedbackRow[];
  pendingReminders: NotificationRow[];
  portalTokens: Map<string, ContractorPortalTokenRow>;
}

/**
 * Load the whole workspace in ONE round of requests.
 *
 * Every query here is independent on purpose. Before 2026-09-22 this was six
 * rounds — four modules each fetched the project's rooms before their own
 * rows, and four more queries ran one after another at the end — which at the
 * ~200ms per-request floor on the live database cost well over a second
 * before anything painted, on every single section click.
 *
 * If you add a query here, it must not depend on the result of another one.
 * `tests/views/project-load-budget.test.ts` fails if the rounds go up.
 */
export async function loadProjectWorkspace(id: string): Promise<ProjectWorkspace | null> {
  // Retiring reminders whose moment has passed is housekeeping, not part of
  // the page: fired, never awaited. The list below hides stale ones anyway
  // (isStaleReminder), so waiting on the write bought nothing but a round trip.
  void cancelStaleNotifications(id, staleCutoffIso()).catch(() => { /* best effort */ });

  const [project, rooms, contacts, notes, roomColors, specs, files, questionnaire, punch, schedule,
    notifications, changeOrders, changeOrderEvents, assignments, materialCategories, budgetCategories,
    budgetLines, projectBudget, openFeedback, pending, portalTokens] = await Promise.all([
    getProject(id),
    listRooms(id),
    listContacts(id),
    listNotes(id),
    listProjectRoomColors(id),
    listSpecsForProject(id),
    listFiles(id),
    getQuestionnaire(id),
    listPunchForProject(id),
    listScheduleForProject(id),
    listNotificationsForProject(id, 20).catch(() => [] as NotificationRow[]),
    listChangeOrders(id).catch(() => [] as ChangeOrderRow[]),
    listChangeOrderEvents(id).catch(() => [] as ChangeOrderEventRow[]),
    listAssignmentsForProject(id).catch(() => [] as ContractorAssignmentRow[]),
    listMaterialCategories().catch(() => [] as MaterialCategoryRow[]),
    listBudgetCategories().catch(() => [] as BudgetCategoryRow[]),
    listBudgetLines(id).catch(() => [] as BudgetLineRow[]),
    getProjectBudget(id).catch(() => null as ProjectBudgetRow | null),
    listOpenFeedbackForProject(id).catch(() => [] as RoomFeedbackRow[]),
    listPendingNotifications(id).catch(() => [] as NotificationRow[]),
    // Bulk-loaded so each contact card can carry its portal URL in the DOM —
    // resolving one at click time would spend the gesture navigator.share()
    // needs.
    listPortalTokensForProject(id).catch(() => new Map<string, ContractorPortalTokenRow>()),
  ]);
  if (!project) return null;

  return {
    p: project, rooms, contacts, notes, roomColors, specs, files, questionnaire, punch, schedule,
    notifications, changeOrders, changeOrderEvents, assignments, materialCategories, budgetCategories,
    budgetLines, projectBudget, openFeedback,
    // The housekeeping write above may not have landed yet, so a reminder
    // whose moment has passed is filtered here rather than waited for.
    pendingReminders: pending.filter((n) => !isStaleReminder(n.scheduled_for)),
    portalTokens,
  };
}

/**
 * How long a loaded workspace is trusted without a refresh. Long enough that
 * clicking through sections costs nothing, short enough that a change made in
 * a room — or by a teammate — shows up on the way back.
 */
export const WORKSPACE_STALE_MS = 15_000;

/** One project at a time: a workspace can be megabytes. */
const cache = new PageCache<ProjectWorkspace>(1);

const key = (id: string): string => `project:${id}`;

export const peekWorkspace = (id: string): ProjectWorkspace | null => cache.peek(key(id));
export const workspaceIsFresh = (id: string): boolean => cache.isFresh(key(id), WORKSPACE_STALE_MS);
export const rememberWorkspace = (id: string, ws: ProjectWorkspace): void => cache.put(key(id), ws);

/**
 * Call after writing anything that belongs to a project — room-detail does,
 * because a room edited there is stale on the project page behind it.
 */
export function forgetProject(id: string): void {
  cache.forget(key(id));
}

/** For writes that could touch any project, like deleting a firm contact. */
export function forgetAllProjects(): void {
  cache.clear();
}
